import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateOrganizationTicketComment } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useAppTranslation } from '@spendbrain/modules/tickets/hooks';
import { z } from 'zod';

export enum TicketDetailsLogFormField {
  Log = 'log',
}
const TICKET_COMMENT_MAX_LENGTH = 256;

export const useLogsTabFormValidationSchema = () => {
  const { t } = useAppTranslation();

  return z.object({
    [TicketDetailsLogFormField.Log]: z
      .string()
      .min(1, {
        message: t('validation.required'),
      })
      .max(TICKET_COMMENT_MAX_LENGTH, {
        message: t('validation.messageCantBeLongerThan', {
          max: TICKET_COMMENT_MAX_LENGTH,
        }),
      }),
  });
};

export type TicketDetailsFormValues = z.infer<
  ReturnType<typeof useLogsTabFormValidationSchema>
>;

export const useLogsTabForm = (ticketId: string) => {
  const validationSchema = useLogsTabFormValidationSchema();
  const { organizationId } = useUserSetupStore();
  const { mutateAsync: createTicketComment } =
    useCreateOrganizationTicketComment(organizationId, ticketId);

  const onSubmitLogs = async (values: TicketDetailsFormValues) =>
    await createTicketComment({ text: values.log });

  const formProps = useForm<
    TicketDetailsFormValues,
    null,
    TicketDetailsFormValues
  >({
    defaultValues: {
      [TicketDetailsLogFormField.Log]: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (values: TicketDetailsFormValues) => {
    await onSubmitLogs(values);
    formProps.reset();
  };

  const onCancel = useCallback(() => {
    formProps.reset();
  }, [formProps]);

  return {
    formProps,
    onSubmit,
    onCancel,
  };
};
